import React from "react";
import CloseIcon from "@mui/icons-material/Close";
import { Dialog, DialogContent, DialogContentText, DialogTitle, IconButton } from "@mui/material";
import useAsyncRequest from "hooks/useAsyncRequest";
import type { ServerLicenseXmlGenerationMode } from "client/api/ServerLicenseApi";
import { getLicenseXmlById } from "client/api/ServerLicenseApi";
import { LicenseKeyXmlView } from "areas/licenses/component/LicenseKeyXmlView";

type LegacyLicenseKeyXmlDialogProps = {
  open: boolean;
  serverLicenseId: string;
  onClose: () => void;
};

// We wrap the API call so we don't load the XML until the dialog is visible
const wrappedApiCall = async (
  serverLicenseId: string,
  generationMode: ServerLicenseXmlGenerationMode,
  makeCall: boolean
) => {
  if (makeCall) return getLicenseXmlById(serverLicenseId, generationMode);
};

export function LegacyLicenseKeyXmlDialog(props: LegacyLicenseKeyXmlDialogProps) {
  const { open, serverLicenseId, onClose } = props;

  const { data, loading, error } = useAsyncRequest(wrappedApiCall, serverLicenseId, "Legacy", open);

  return (
    <Dialog
      open={open}
      maxWidth={"md"}
      onClose={onClose}
      role="dialog"
      aria-labelledby="legacy-license-key-xml-dialog-title"
    >
      <DialogTitle id="legacy-license-key-xml-dialog-title">
        Legacy license key XML
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          In v2019.10 we updated the way we reason about licensing to match the new "subscription terms" that apply to
          modern license keys. This means that unless a customer is within a "valid subscription period", we prevent
          them from deploying. Modern subscription keys use a <code>ValidTo</code> date to govern this period.
        </DialogContentText>
        <DialogContentText sx={{ mb: 2 }}>
          However, in versions of Octopus Server &lt; 2019.10 there is no code to make this distinction. This can cause
          a bug (for licenses that were created &gt; 1 year before today) that shows "License Expired" on the licensing
          page when using a valid modern license. To circumvent this, you can provide the "compliant XML" shown below
          -&gt; this just uses <code>MaintenanceExpires</code> instead so that server is happy.{" "}
        </DialogContentText>
        <DialogContentText sx={{ mb: 2 }}>
          <strong>
            This won't work for any versions <code>3.x</code> though!
          </strong>{" "}
          Before we shipped v4, the licensing code "only" interpreted licenses using the "things" method, and the
          complaint XML below won't necessarily for this either. Ask a question in #license-system-requests on slack for
          options to solve this - or ask the customer to upgrade.
        </DialogContentText>
        <LicenseKeyXmlView loading={loading} licenseXml={data?.licenseXml} error={error} />
      </DialogContent>
    </Dialog>
  );
}
